import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from '../contexts/I18n';
import { useAuth } from '../contexts/AuthContext';
import { Link, useParams, useLocation } from "react-router-dom";
import ELogo from './ELogo';
import "./Navbar.css";

/* =========================================
   VARIANT SWITCH
   ========================================= */
export default function Navbar() {
  const { pathname } = useLocation();
  const isAuth = /^\/(login|register|forgot-password)(?:\/|$)/i.test(pathname);
  return isAuth ? <AuthNavbar /> : <DefaultNavbar />;
}

/* =========================================
   SHARED SERVICE BOXES
   ========================================= */
const serviceBoxes = [
  { title: "Barber" },
  { title: "Hair & Nail" },
  { title: "Laser Clinics" },
  { title: "Makeup" },
  { title: "Massage" },
  { title: "Pet Services" },
];

// Basic Arabic display labels for services
const svcTranslations = {
  "Barber": "حلاق",
  "Hair & Nail": "صالون شعر وأظافر",
  "Laser Clinics": "عيادات الليزر",
  "Makeup": "مكياج",
  "Massage": "مساج",
  "Pet Services": "خدمات الحيوانات الأليفة",
};

// Map display titles → slugs
const slugMap = {
  "Barber": "barber",
  "Hair & Nail": "hair-&-nail",
  "Laser Clinics": "laser-clinics",
  "Makeup": "makeup",
  "Massage": "massage",
  "Pet Services": "pet-services",
};

// Service images mapping
const serviceImages = {
  "Barber": "/images/barbershop.jpg",
  "Hair & Nail": "/images/hair.jpg",
  "Laser Clinics": "/images/hair1.jpg",
  "Makeup": "/images/makeup.jpg",
  "Massage": "/images/massage.jpg",
  "Pet Services": "/images/pet.png",
};

/* =========================================
   AUTH VARIANT — /login, /register & /forgot-password
   ========================================= */
export function AuthNavbar() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);

  // Prevent background scroll while sheet is open
  useEffect(() => {
    document.body.classList.toggle("nav-no-scroll", open);
    return () => document.body.classList.remove("nav-no-scroll");
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ServicesGrid = ({ id }) => (
    <div id={id} className="services-grid" aria-label="Service categories">
      {serviceBoxes.map(({ title }) => {
        const slug = slugMap[title];
        const img = serviceImages[title] || "/images/shop-placeholder.png";
        const label = lang === 'ar' ? (svcTranslations[title] || title) : title;
        return (
          <Link
            key={title}
            to={`/service/${slug}`}
            className="service-btn"
            onClick={() => setOpen(false)}
          >
            <img src={img} alt="" className="service-btn-image" />
            <span className="service-title">{label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      <header className="authbar" role="navigation" aria-label="Auth navbar">
        <div className="authbar-inner">
          <Link to="/" className="authbrand" aria-label={t('home')}>
            <ELogo size="small" />
          </Link>

          <button
            className="cat-trigger"
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls="auth-nav-sheet"
            onClick={() => setOpen((v) => !v)}
          >
            {t('categories')} ▾
          </button>
        </div>
      </header>

      {/* Overlay (click to close) */}
      {open && (
        <div
          className="nav-overlay auth-nav-overlay"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Categories sheet — used on mobile and laptop for auth pages */}
      <aside
        id="auth-nav-sheet"
        className={`nav-sheet auth-nav-sheet ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('categories')}
      >
        <div className="nav-sheet-head">
          <div className="nav-sheet-heading">
            <h3 className="nav-sheet-title">{t('categories')}</h3>
            <p className="nav-sheet-subtitle">
              {lang === 'ar' ? 'اختر الخدمة المناسبة لك' : 'Pick a service to get started'}
            </p>
          </div>
          <button
            className="nav-sheet-close"
            aria-label={t('closeMenu')}
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        <ServicesGrid id="auth-nav-sheet-grid" />
      </aside>
    </>
  );
}

/* =========================================
   DEFAULT VARIANT — All other pages
   ========================================= */
function DefaultNavbar() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const { town } = useParams();
  const isServiceCategory = /^\/service\//i.test(pathname);
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  // Build link path that respects /town/:town if present
  const pathFor = useCallback(
    (slug) => {
      const base = `/service/${slug}`;
      return town ? `${base}/town/${town}` : base;
    },
    [town]
  );

  // Prevent background scroll while sheet is open
  useEffect(() => {
    document.body.classList.toggle("nav-no-scroll", open);
    return () => document.body.classList.remove("nav-no-scroll");
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ServicesGrid = ({ id }) => (
    <div id={id} className="services-grid" aria-label="Service categories">
      {serviceBoxes.map(({ title }) => {
        const slug = slugMap[title];
        const img = serviceImages[title] || "/images/shop-placeholder.png";
        const label = lang === 'ar' ? (svcTranslations[title] || title) : title;
        return (
          <Link
            key={title}
            to={pathFor(slug)}
            className="service-btn"
            onClick={() => setOpen(false)}
          >
            <img src={img} alt="" className="service-btn-image" />
            <span className="service-title">{label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Fixed brand logo — links home (replaces back/home icon on all pages) */}
      <Link
        to={isServiceCategory ? "/" : (isAuthenticated ? "/dashboard" : "/")}
        className="home-btn home-btn--brand"
        aria-label={t('home')}
      >
        <ELogo size="small" />
      </Link>

      {/* Mobile Hamburger */}
      <button
        className={`nav-toggle-fixed ${open ? "is-open" : ""}`}
        aria-label={open ? t('closeMenu') : t('openMenu')}
        aria-expanded={open}
        aria-controls="nav-sheet"
        onClick={() => setOpen((p) => !p)}
      >
        <span className="nav-toggle-icon" aria-hidden="true">
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
        </span>
      </button>

      {/* Desktop/Tablet navbar strip */}
      <header className="navbar-container" role="navigation" aria-label="Primary">
        <nav className="services-wrapper">
          <ServicesGrid />
        </nav>
        <div style={{ marginInlineStart: 'auto', paddingInline: 12 }} />
      </header>

      {/* Overlay (click to close) */}
      {open && <div className="nav-overlay" onClick={() => setOpen(false)} />}

      {/* Mobile slide-down sheet */}
      <aside
        id="nav-sheet"
        className={`nav-sheet ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('categories')}
      >
        <div className="nav-sheet-head">
          <div className="nav-sheet-heading">
            <h3 className="nav-sheet-title">{t('categories')}</h3>
            <p className="nav-sheet-subtitle">
              {lang === 'ar' ? 'اختر الخدمة المناسبة لك' : 'Pick a service to get started'}
            </p>
          </div>
          <button
            className="nav-sheet-close"
            aria-label={t('closeMenu')}
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        <ServicesGrid id="nav-sheet-grid" />

        <div className="nav-sheet-actions">
          <Link to="/register" onClick={() => setOpen(false)}>
            {t('signUp')}
          </Link>
          {isAuthenticated ? (
            <Link to="/dashboard" onClick={() => setOpen(false)}>
              {t('dashboard')}
            </Link>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)}>
              {t('login')}
            </Link>
          )}
        </div>
      </aside>
    </>
  );
}
